/*
 * Largely copied from https://github.com/gsklee/ngStorage
 * Moved to an internal service so we could manage it easier
 */

function isStorageSupported($window, storageType) {
  // Some installations of IE, for an unknown reason, throw "SCRIPT5: Error: Access is denied"
  // when accessing window.localStorage. This happens before you try to do anything with it. Catch
  // that error and allow execution to continue.

  // fix 'SecurityError: DOM Exception 18' exception in Desktop Safari, Mobile Safari
  // when "Block cookies": "Always block" is turned on
  let supported;
  try {
    supported = $window[storageType];
  } catch (err) {
    supported = false;
  }

  // When Safari (OS X or iOS) is in private browsing mode, it appears as though localStorage
  // is available, but trying to call .setItem throws an exception below:
  // "QUOTA_EXCEEDED_ERR: DOM Exception 22: An attempt was made to add something to storage that exceeded the quota."
  if (supported && storageType === 'localStorage') {
    const key = '__' + Math.round(Math.random() * 1e7);

    try {
      localStorage.setItem(key, key);
      localStorage.removeItem(key);
    } catch (err) {
      supported = false;
    }
  }

  return supported;
}

/**
 * @ngdoc overview
 * @name ngStorage
 */

export const storageServiceModule = angular
  .module('Q.services.storage', [])

  /**
   * @ngdoc object
   * @name ngStorage.$localStorage
   * @requires $rootScope
   * @requires $window
   */
  .factory('$localStorage', _storageProvider('localStorage'))

  /**
   * @ngdoc object
   * @name ngStorage.$sessionStorage
   * @requires $rootScope
   * @requires $window
   */
  .factory('$sessionStorage', _storageProvider('sessionStorage'));

function _storageProvider(storageType) {
  return /* @ngInject */ function (
    $rootScope,
    $window,
    $log,
    $timeout,
    $document,
  ) {
    let errorReported = false;

    const storageKeyPrefix = 'ngStorage-';

    const serializer = angular.toJson;
    const deserializer = angular.fromJson;

    // The magic number 10 is used which only works for some keyPrefixes...
    // See https://github.com/gsklee/ngStorage/issues/137
    const prefixLength = storageKeyPrefix.length;
    let _last$storage;
    let _debounce;

    // #9: Assign a placeholder object if Web Storage is unavailable to prevent breaking the entire AngularJS app
    // Note: recheck mainly for testing (so we can use $window[storageType] rather than window[storageType])
    const isSupported = isStorageSupported($window, storageType);
    let webStorage = isSupported;
    if (!isSupported) {
      // warn on page load
      $window.alert(
        'Unable to use "localStorage". It is possible that cookies are disabled, this is a private tab, or there is some other storage issue.\n\nYou can still use Qualified, but you may have to re-log in every time you load it, and you could lose data.',
      );
      $log.warn('Issue with this browser and web storage');
      webStorage = {
        setItem: angular.noop,
        getItem: angular.noop,
        removeItem: angular.noop,
      };
    }
    const $storage = {
      $sync() {
        let i = 0;
        const l = webStorage.length;
        let k;
        for (; i < l; i++) {
          // #8, #10: `webStorage.key(i)` may be an empty string (or throw an exception in IE9 if `webStorage` is empty)
          (k = webStorage.key(i)) &&
            storageKeyPrefix === k.slice(0, prefixLength) &&
            ($storage[k.slice(prefixLength)] = deserializer(
              webStorage.getItem(k),
            ));
        }
      },
      $apply() {
        try {
          let temp$storage;

          _debounce = null;

          if (!angular.equals($storage, _last$storage)) {
            temp$storage = angular.copy(_last$storage);
            angular.forEach($storage, function (v, k) {
              if (angular.isDefined(v) && '$' !== k[0]) {
                webStorage.setItem(storageKeyPrefix + k, serializer(v));
                delete temp$storage[k];
              }
            });

            for (const k in temp$storage) {
              webStorage.removeItem(storageKeyPrefix + k);
            }

            _last$storage = angular.copy($storage);
          }
        } catch (ex) {
          // angular.copy may throw an error:
          // TypeError: Attempted to assign to readonly property.
          if (!errorReported) {
            errorReported = true;
            $log.error(ex);
          } else {
            // once we've reported storage errors, only log the error to the console
            $log.warn(ex);
          }
        }
      },
    };

    $storage.$sync();

    _last$storage = angular.copy($storage);

    $rootScope.$watch(function () {
      _debounce || (_debounce = $timeout($storage.$apply, 100, false));
    });

    // #6: Use `$window.addEventListener` instead of `angular.element` to avoid the jQuery-specific `event.originalEvent`
    $window.addEventListener &&
      $window.addEventListener('storage', function (event) {
        if (!event.key) {
          return;
        }

        const doc = $document[0];

        if (
          (!doc.hasFocus || !doc.hasFocus()) &&
          storageKeyPrefix === event.key.slice(0, prefixLength)
        ) {
          event.newValue
            ? ($storage[event.key.slice(prefixLength)] = deserializer(
                event.newValue,
              ))
            : delete $storage[event.key.slice(prefixLength)];

          _last$storage = angular.copy($storage);

          // trigger an apply if necessary
          $rootScope.$applyAsync();
        }
      });

    $window.addEventListener &&
      $window.addEventListener('beforeunload', function () {
        $storage.$apply();
      });

    return $storage;
  };
}
